import { PricingCoreFeaturesData } from "@/components/Home/v2/PricingCoreFeaturesSection";
import { PricingHeroV2Data } from "@/components/Home/v2/PricingHeroV2";
import { PricingPlansCompareData } from "@/components/Home/v2/PricingPlansCompareSection";
import { ButtonSizeEnum, ButtonTypeEnum } from "@/components/atoms/Button";
import {
  BookADemoSourceEnum,
  CtaActionType,
  CtaType,
  SignUpSourceEnum,
} from "@/components/molecules/CtaButtonGroup";
import { ProductV2FaqSection } from "@/shared/data/product.v2.data";
import {
  heroPrimaryCta,
  heroSecondaryCta,
} from "@/shared/data/product.v2.data";
import { LANDING_PAGE_EVENTS } from "@/constants/amplitude.constants";
import { getCDNURL } from "@/shared/utils/commons";

const planGetStartedCtaUs: CtaType = {
  type: ButtonTypeEnum.Primary,
  size: ButtonSizeEnum.Regular,
  text: "Get Started",
  actionType: CtaActionType.SignUp,
  amplitudeEvent: {
    eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
    eventProperties: {
      source: SignUpSourceEnum.hero,
    },
  },
};

const scheduleCallCtaUs: CtaType = {
  type: ButtonTypeEnum.Primary,
  size: ButtonSizeEnum.Regular,
  text: "Schedule a Call",
  actionType: CtaActionType.DelayedHrefBlank,
  href: "https://calendly.com/d/cx9y-wsb-7n5/graphy-product-demo-enterprises?",
  amplitudeEvent: {
    eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
    eventProperties: {
      source: BookADemoSourceEnum.bodyEnd,
    },
  },
};

const bookDemoOnlyCtaUs: CtaType = {
  type: ButtonTypeEnum.Primary,
  size: ButtonSizeEnum.Regular,
  text: "Book A Demo",
  actionType: CtaActionType.BookDemo,
  amplitudeEvent: {
    eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
    eventProperties: {
      source: BookADemoSourceEnum.bodyEnd,
    },
  },
};

// --- 1. HERO ---------------------------------------------------------------

export const pricingV2HeroDataUs: PricingHeroV2Data = {
  titleMuted: "Start Your",
  titleBright: "14-Day Free Trial Today",
  description:
    "Whether you're a solo creator or a team of 10, Graphy gives you everything you need to launch, scale, and automate your online teaching business — all in one place.",
  bullets: [
    "AI Website Builder",
    "Tools to build and sell digital products",
    "Branded mobile apps",
    "AI sales, support & marketing agents",
    "Marketing campaign automations",
    "Secure payment gateway and CRM",
  ],
  trustedByLabel: "Trusted by 200,000+ creators",
  reviewBadges: [
    { name: "G2 Crowd", rating: 4.3 },
    { name: "Capterra", rating: 4.7 },
    { name: "Trustpilot", rating: 4.1 },
  ],
  card: {
    image: `${getCDNURL()}graphy-web/V4/images/Pricing Page/Pricing Page US images/hero section card/hero section card image.png`,
    imageAlt: "Graphy creator",
    startsAtLabel: "Starts at",
    strikePrice: "$399",
    freeLabel: "Free",
    durationLabel: "for 14 days",
    ctas: [heroPrimaryCta, heroSecondaryCta],
  },
};

// --- 2. COMPARE PLANS ------------------------------------------------------

export const pricingV2PlansDataUs: PricingPlansCompareData = {
  eyebrow: "PRICING",
  title: "Compare Plans",
  subtitle:
    "Whether you're just starting or scaling fast, there's a plan built for you. No fine print. No hidden limits. Just choose your plan and start.",
  showToggle: true,
  plans: [
    {
      name: "Launch",
      tagline: "Tailored for creators",
      price: "$49",
      pricePeriod: "Per Month",
      annualPrice: "$449",
      annualPricePeriod: "Per Year",
      feeLine: "0% transaction fee",
      description:
        "Launch your digital products — with everything you need to start selling online.",
      cta: planGetStartedCtaUs,
      featuresHeading: "All core features, plus:",
      features: [
        { label: "AI Website Builder", icon: "sparkles" },
        { label: "2 AI Custom Webpages", icon: "sparkles" },
        { label: "0% transaction fee", icon: "check" },
        { label: "Unlimited Products", icon: "check" },
        { label: "DRM Protection", icon: "check" },
        { label: "10,000 Contacts", icon: "check" },
        { label: "1 Admin Account", icon: "check" },
        { label: "Up to 500 Webinar Participants", icon: "check" },
        { label: "10,000 Live Class Minutes/Month", icon: "check" },
        { label: "5,000 Marketing Emails/Month", icon: "check" },
        { label: "Third Party Integrations", icon: "check" },
      ],
    },
    {
      name: "Rise",
      tagline: "Built for businesses",
      price: "$149",
      pricePeriod: "Per Month",
      annualPrice: "$1,349",
      annualPricePeriod: "Per Year",
      feeLine: "0% transaction fee",
      description:
        "Scale your business with advanced AI tools designed to convert and retain customers.",
      cta: planGetStartedCtaUs,
      featuresHeading: "Everything in Launch, plus:",
      recommended: true,
      features: [
        { label: "AI Agent for Sales", icon: "sparkles" },
        { label: "AI Agent for Support", icon: "sparkles" },
        { label: "AI Agent for Tutoring", icon: "sparkles" },
        { label: "AI Landing Pages", icon: "sparkles" },
        { label: "Unlimited AI Custom Pages", icon: "sparkles" },
        { label: "Both Android and iOS Apps", icon: "check" },
        { label: "25,000 Contacts", icon: "check" },
        { label: "10,000 Marketing Emails/Month", icon: "check" },
        { label: "10 Admin Accounts", icon: "check" },
        { label: "20,000 Live Class Minutes/Month", icon: "check" },
        { label: "Your own branded mobile app", icon: "check" },
      ],
    },
    {
      name: "Scale",
      tagline: "Suited for brands",
      price: "$399",
      pricePeriod: "Per Month",
      annualPrice: "$3,599",
      annualPricePeriod: "Per Year",
      feeLine: "0% transaction fee",
      description:
        "Run a high-volume and fully branded business — built for scale, security, and performance.",
      cta: planGetStartedCtaUs,
      featuresHeading: "Everything in Rise, plus:",
      features: [
        { label: "Advanced DRM Protection", icon: "check" },
        { label: "100,000 Contacts", icon: "check" },
        { label: "50,000 Marketing Emails/Month", icon: "check" },
        { label: "25 Admin Accounts", icon: "check" },
        { label: "50,000 Live Class Minutes/Month", icon: "check" },
        { label: "API & SSO Support", icon: "check" },
        { label: "All advanced integrations", icon: "check" },
        { label: "Migration Assistance", icon: "check" },
        { label: "Priority Support", icon: "check" },
        { label: "Remove Graphy Branding", icon: "check" },
        { label: "Personalized Onboarding Assistance", icon: "check" },
      ],
    },
  ],
};

// --- 3. CORE FEATURES -------------------------------------------------------

export const pricingV2CoreFeaturesDataUs: PricingCoreFeaturesData = {
  title: "Every plan includes core features",
  features: [
    "AI Website Builder",
    "Real-time sales and conversion tracking",
    "Affiliate tracking baked right in",
    "Deep marketing insights via built-in analytics",
    "Accept payments via Stripe or your preferred gateway",
    "SSL certificates for platform security",
    "Run discounts on products",
    "Global selling with country-specific pricing",
    "Custom team roles and admin access",
    "Offer exclusive memberships to superfans",
    "Free custom domain support",
    "Unlimited live sessions and webinars",
    "Award certificates to learners — automatically",
    "Create custom coupon codes",
  ],
  ctas: [heroPrimaryCta, heroSecondaryCta],
};

// --- 4. FAQ ----------------------------------------------------------------

export const pricingV2FaqDataUs: ProductV2FaqSection = {
  title: "Frequently Asked Questions",
  ctas: [heroPrimaryCta, heroSecondaryCta],
  items: [
    {
      question: "Is there a free trial?",
      answer:
        "Yes, every plan comes with a 14-day free trial. No credit card required to start.",
    },
    {
      question: "Is there a refund policy?",
      answer:
        "Yes, if you end up not finding a use case for the platform, we offer a refund window of 7 days, no questions asked.",
    },
    {
      question: "How can I pay for my Graphy account?",
      answer:
        "We accept all major credit cards and payment methods. You can manage your subscription from your Graphy dashboard.",
    },
    {
      question: "Are there any discounts for annual billing?",
      answer:
        "Yes! Annual plans come with 25% off compared to monthly billing. Switch to annual on the pricing page to see your discounted rate.",
    },
    {
      question: "How does the payout work?",
      answer:
        "All your earnings from your sales will be instantly credited into your account.",
    },
  ],
};
